// ═══════════════════════════════════════════════════════════════
// Director Task Manager — Type Definitions
// Path: src/constants/taskTypes.js
// ═══════════════════════════════════════════════════════════════

export const TASK_PRIORITIES = [
  { id: "high", name: "High", color: "#ef4444", icon: "AlertCircle", sortOrder: 1 },
  { id: "normal", name: "Normal", color: "#3b82f6", icon: "Circle", sortOrder: 2 },
  { id: "low", name: "Low", color: "#6b7280", icon: "ArrowDown", sortOrder: 3 },
];

export const TASK_CATEGORIES = [
  { id: "follow_up", name: "Follow Up", color: "#f59e0b", icon: "PhoneCall" },
  { id: "proposal", name: "Proposal", color: "#8b5cf6", icon: "FileText" },
  { id: "acknowledgment", name: "Acknowledgment", color: "#10b981", icon: "CheckCircle" },
  { id: "thank", name: "Thank", color: "#ec4899", icon: "Heart" },
  { id: "report", name: "Report", color: "#3b82f6", icon: "BarChart3" },
  { id: "renewal", name: "Renewal", color: "#ef4444", icon: "RefreshCw" },
  { id: "meeting", name: "Meeting", color: "#0ea5e9", icon: "Users" },
  { id: "general", name: "General", color: "#6b7280", icon: "ClipboardList" },
];

export const TASK_STATUSES = [
  { id: "open", name: "Open" },
  { id: "completed", name: "Completed" },
];

// Auto-task templates: when a portfolio move fires, create this task
export const MOVE_TASK_MAP = {
  cultivate: { title: "Schedule cultivation touch with {donor}", category: "follow_up", dueDays: 7 },
  propose: { title: "Draft proposal for {donor}", category: "proposal", dueDays: 14 },
  acknowledge: { title: "Send acknowledgment to {donor}", category: "acknowledgment", dueDays: 3 },
  thank: { title: "Send thank-you to {donor}", category: "thank", dueDays: 3 },
  report: { title: "Prepare impact report for {donor}", category: "report", dueDays: 14 },
  renew: { title: "Begin renewal conversation with {donor}", category: "renewal", dueDays: 7 },
};

// ── Recurrence definitions ──────────────────────────────────────
// The "none" row is a UI-only sentinel — when selected, the modal
// treats the task as one-off and never writes a task_recurrences row.

export const TASK_FREQUENCIES = [
  { id: "none",      name: "Doesn't repeat" },
  { id: "daily",     name: "Daily" },
  { id: "weekly",    name: "Weekly" },
  { id: "monthly",   name: "Monthly" },
  { id: "quarterly", name: "Quarterly" },
  { id: "yearly",    name: "Yearly" },
  { id: "custom",    name: "Every N days…" },
];

export const TASK_END_TYPES = [
  { id: "never", name: "Never" },
  { id: "count", name: "After N occurrences" },
  { id: "date",  name: "On a specific date" },
];

// Clamp a JS Date to the end of its month if it would roll forward
// (e.g. Jan 31 + 1 month is Feb 28/29, not Mar 3).
const clampToMonthEnd = (year, monthIndex, day) => {
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  return new Date(year, monthIndex, Math.min(day, lastDay));
};

const toIsoDate = (dateObj) => {
  const yyyy = dateObj.getFullYear();
  const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
  const dd = String(dateObj.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Given a completed instance's due date (or today, if none) + a
// recurrence rule, returns the ISO date (YYYY-MM-DD) the next
// occurrence should be due. Returns null if the rule is invalid.
export const computeNextDueDate = (anchorIso, frequency, intervalValue = 1) => {
  const base = anchorIso ? new Date(anchorIso + "T00:00:00") : new Date();
  if (isNaN(base.getTime())) return null;

  const y = base.getFullYear();
  const m = base.getMonth();
  const d = base.getDate();

  let next;
  switch (frequency) {
    case "daily":
      next = new Date(y, m, d + 1);
      break;
    case "weekly":
      next = new Date(y, m, d + 7);
      break;
    case "monthly":
      next = clampToMonthEnd(y, m + 1, d);
      break;
    case "quarterly":
      next = clampToMonthEnd(y, m + 3, d);
      break;
    case "yearly":
      next = clampToMonthEnd(y + 1, m, d);
      break;
    case "custom": {
      const step = Math.max(1, parseInt(intervalValue, 10) || 1);
      next = new Date(y, m, d + step);
      break;
    }
    default:
      return null;
  }
  return toIsoDate(next);
};

// Evaluate whether the series has reached its end condition given the
// spawned-count *before* a hypothetical next spawn. Returns true if
// the next spawn should be skipped.
export const recurrenceShouldStop = (recurrence) => {
  if (!recurrence) return true;
  if (recurrence.isActive === false) return true;
  if (recurrence.endType === "count") {
    const cap = parseInt(recurrence.endCount, 10);
    if (!Number.isFinite(cap)) return false;
    return (recurrence.occurrencesSpawned || 0) >= cap;
  }
  if (recurrence.endType === "date" && recurrence.endDate) {
    const todayIso = toIsoDate(new Date());
    return todayIso > recurrence.endDate;
  }
  return false;
};
